import React, { PropsWithChildren, ReactElement } from "react";
import * as DialogRU from "@radix-ui/react-dialog";
import styles from "./carousel.module.scss";
import classNames from "classnames";
import { Swiper, SwiperSlide, SwiperProps } from "swiper/react";
import { Pagination, Navigation, Keyboard } from "swiper/modules";
import { merge } from "lodash-es";

const Root = (props: DialogRU.DialogProps) => {
  return <DialogRU.Root {...props} />;
};

const Trigger = (props: DialogRU.DialogTriggerProps) => {
  const { children, ...rest } = props;
  return <DialogRU.Trigger {...rest}>{children}</DialogRU.Trigger>;
};

const Overlay = (
  props: DialogRU.DialogOverlayProps & {
    closeOnClick?: () => void;
  },
): ReactElement => {
  const { className, closeOnClick, onClick, ...rest } = props;

  const handleClick = (e) => {
    if (closeOnClick) {
      closeOnClick();
      return;
    }
    onClick && onClick(e);
  };

  return (
    <DialogRU.Overlay
      className={classNames(className, styles.overlay)}
      onClick={handleClick}
      {...rest}
    />
  );
};

const Portal = DialogRU.Portal;

const Content = (props: DialogRU.DialogContentProps): ReactElement => {
  const { children, className, ...rest } = props;
  return (
    <DialogRU.Content
      className={classNames(className, styles.content)}
      {...rest}
    >
      {children}
    </DialogRU.Content>
  );
};

const CarouselInstance = React.forwardRef(
  (
    props: PropsWithChildren<{
      className?: string;
      swiperOptions?: Omit<SwiperProps, "className">;
      size: "md" | "lg";
      variant: "dark" | "light";
      type: "line" | "dot";
      withFrame: boolean;
      color: string;
    }>,
    ref: any,
  ) => {
    const { className, swiperOptions, ...rest } = props;

    const baseConfig: SwiperProps = {
      modules: [Pagination, Navigation, Keyboard],
      navigation: {
        nextEl: ".carousel_button_next",
        prevEl: ".carousel_button_prev",
      },
      pagination: {
        el: ".carousel-bullet-wrapper",
        clickable: true,
        bulletClass: classNames(
          styles.bullet,
          styles[props.type],
          styles[`variant-${props.variant}-${props.color}`],
          {
            [styles.md]: rest.size === "md",
            [styles.lg]: rest.size === "lg",
          },
        ),
        bulletActiveClass: classNames(
          styles[`active-variant-${props.variant}-${props.color}`],
        ),
        type: "bullets",
      },
      slidesPerView: "auto",
      loop: false,
      autoHeight: true,
    };
    const mergedConfig = merge(baseConfig, swiperOptions);

    return (
      <Swiper
        className={classNames(className, styles.carouselInstance)}
        {...mergedConfig}
        ref={ref}
      >
        {props.children}

        <div
          className={classNames(
            styles.bulletWrapper,
            styles[`variant-${props.variant}-${props.color}`],
            {
              [styles.md]: props.size === "md",
              [styles.lg]: props.size === "lg",
            },
            "carousel-bullet-wrapper",
            {
              [styles.withFrame]: props.withFrame,
            },
          )}
        />
      </Swiper>
    );
  },
);
CarouselInstance.displayName = "CarouselInstance";

const Slide = SwiperSlide;

const PrevNextButtonBase = React.forwardRef(
  (
    props: PropsWithChildren<
      React.ButtonHTMLAttributes<HTMLButtonElement> & {
        size: "md" | "lg";
        variant: "dark" | "light";
        color: string;
      }
    >,
    ref: any,
  ) => {
    return (
      <button
        type="button"
        className={classNames(
          props.className,
          styles.prevNextButton,
          styles[`prevnext-variant-${props.variant}-${props.color}`],
          {
            [styles.md]: props.size === "md",
            [styles.lg]: props.size === "lg",
          },
        )}
        onClick={props.onClick}
        id={props.id}
        ref={ref}
      >
        {props.children}
      </button>
    );
  },
);
PrevNextButtonBase.displayName = "PrevNextButtonBase";

const PrevButton = React.forwardRef(
  (
    props: PropsWithChildren<
      React.ButtonHTMLAttributes<HTMLButtonElement> & {
        size: "md" | "lg";
        variant: "dark" | "light";
        color: string;
      }
    >,
    ref: any,
  ) => {
    return (
      <PrevNextButtonBase
        className={classNames(
          props.className,
          styles.prev,
          "carousel_button_prev",
        )}
        size={props.size}
        variant={props.variant}
        onClick={props.onClick}
        color={props.color}
        id={props.id}
        ref={ref}
      >
        {props.children}
      </PrevNextButtonBase>
    );
  },
);
PrevButton.displayName = "PrevButton";

const NextButton = React.forwardRef(
  (
    props: PropsWithChildren<
      React.ButtonHTMLAttributes<HTMLButtonElement> & {
        size: "md" | "lg";
        variant: "dark" | "light";
        color: string;
      }
    >,
    ref: any,
  ) => {
    return (
      <PrevNextButtonBase
        className={classNames(
          props.className,
          styles.next,
          "carousel_button_next",
        )}
        size={props.size}
        variant={props.variant}
        onClick={props.onClick}
        color={props.color}
        id={props.id}
        ref={ref}
      >
        {props.children}
      </PrevNextButtonBase>
    );
  },
);
NextButton.displayName = "NextButton";

const Close = DialogRU.Close;

export const Carousel = {
  Root,
  Trigger,
  Overlay,
  Portal,
  Content,
  CarouselInstance,
  Slide,
  PrevButton,
  NextButton,
  Close,
};
